import { useState, useCallback, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { RefreshCw, Download } from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import {
  getFeatureAdoption,
  getFeatureAdoptionTrends,
  type FeatureUsage,
  type FeatureAdoptionTrend,
} from '../../../lib/api'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import { CHART_PALETTE } from '../../../lib/chart-colors'
import TrendChart from '../../../components/analytics/TrendChart'
import PeriodSelector from '../../../components/analytics/PeriodSelector'
import { cn } from '../../../lib/utils'
import { arrayToCSV, downloadCSV } from '../../../lib/csv-export'

const NAV_TABS = [
  { path: '/admin/analytics', label: 'Overview' },
  { path: '/admin/analytics/users', label: 'Users' },
  { path: '/admin/analytics/apps', label: 'Apps' },
  { path: '/admin/analytics/activity', label: 'Activity' },
  { path: '/admin/analytics/features', label: 'Features' },
  { path: '/admin/analytics/system', label: 'System' },
]

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export default function FeatureAdoption() {
  const location = useLocation()
  const [period, setPeriod] = useState<string>('30d')
  const [features, setFeatures] = useState<FeatureUsage[]>([])
  const [trends, setTrends] = useState<FeatureAdoptionTrend[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [featuresData, trendsData] = await Promise.all([
        getFeatureAdoption(period),
        getFeatureAdoptionTrends(period),
      ])
      setFeatures(featuresData)
      setTrends(trendsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature adoption data')
      throw err
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useAutoRefresh(fetchData, {
    baseInterval: 60000,
    maxInterval: 300000,
  })

  function handleExportCSV() {
    if (features.length === 0) return
    const csv = arrayToCSV(features, [
      { key: 'feature', label: 'Feature' },
      { key: 'distinct_users', label: 'Distinct Users' },
      { key: 'event_count', label: 'Total Events' },
    ])
    downloadCSV(csv, `analytics-features-${period}.csv`)
  }

  // Transform flat trends array into pivoted data for multi-line chart
  const trendData = useMemo(() => {
    const byDate: Record<string, Record<string, number | string>> = {}
    for (const t of trends) {
      if (!byDate[t.date]) byDate[t.date] = { date: t.date }
      byDate[t.date][t.feature] = t.event_count
    }
    return Object.values(byDate).sort((a, b) =>
      (a.date as string).localeCompare(b.date as string)
    )
  }, [trends])

  // Build dynamic line config from unique feature names
  const featureNames = useMemo(
    () => [...new Set(trends.map((t) => t.feature))],
    [trends]
  )
  const trendLines = useMemo(
    () =>
      featureNames.map((f, i) => ({
        dataKey: f,
        label: capitalize(f),
        color: CHART_PALETTE[i % CHART_PALETTE.length],
      })),
    [featureNames]
  )

  // Sort features by distinct_users descending for display
  const sortedFeatures = useMemo(
    () => [...features].sort((a, b) => b.distinct_users - a.distinct_users),
    [features]
  )

  // Prepare bar chart data with capitalized names
  const barData = useMemo(
    () =>
      sortedFeatures.map((f) => ({
        ...f,
        displayName: capitalize(f.feature),
      })),
    [sortedFeatures]
  )

  const totalEvents = useMemo(
    () => features.reduce((sum, f) => sum + f.event_count, 0),
    [features]
  )

  return (
    <div>
      {/* Sub-navigation tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-border">
        {NAV_TABS.map((tab) => (
          <Link
            key={tab.path}
            to={tab.path}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              location.pathname === tab.path
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            )}
          >
            {tab.label}
          </Link>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Feature Adoption</h1>
          <p className="text-muted-foreground">
            Feature usage patterns and adoption trends across the platform
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <button
            onClick={handleExportCSV}
            disabled={features.length === 0}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
            title="Download data as CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && features.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : features.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No feature usage data for this period
        </div>
      ) : (
        <div className="space-y-8">
          {/* Feature adoption trend lines chart */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-1">Feature Adoption Trends</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Daily event count per feature area
            </p>
            {trendData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground">
                No trend data available
              </div>
            ) : (
              <TrendChart data={trendData} lines={trendLines} height={320} />
            )}
          </div>

          {/* Feature usage bar chart */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Users Per Feature Area</h2>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barData}
                  margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="displayName"
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={45}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      borderColor: 'hsl(var(--border))',
                      borderRadius: '0.5rem',
                      color: 'hsl(var(--card-foreground))',
                    }}
                  />
                  <Bar dataKey="distinct_users" radius={[4, 4, 0, 0]}>
                    {barData.map((_, index) => (
                      <Cell
                        key={index}
                        fill={CHART_PALETTE[index % CHART_PALETTE.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Feature usage table */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Feature Usage Details</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Feature
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Distinct Users
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Total Events
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFeatures.map((f) => (
                    <tr
                      key={f.feature}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-3 px-4 font-medium">{capitalize(f.feature)}</td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {f.distinct_users}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {f.event_count.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Total events tracked: {totalEvents.toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
